import { show, showByDate } from "~/api/contractSpendingTotal";
import * as types from "../mutation-types";
import store from "../../store";
import {
    STORAGE as BALANCE_ENTITY_TYPE_STORAGE,
    DATABASE as BALANCE_ENTITY_TYPE_DATABASE,
    SITE as BALANCE_ENTITY_TYPE_SITE,
    CERTIFICATE as BALANCE_ENTITY_TYPE_CERTIFICATE,
    getBalanceEntityTypeMeta
} from "~/constants/contract/balance/type";
import {
    normalizeBalanceEntities,
    getBalanceEntityValue
} from "~/constants/contract/balance/spendingEntities";

function resolvePieEntitySeriesMeta() {
    return [
        {
            typeId: BALANCE_ENTITY_TYPE_STORAGE,
            name: getBalanceEntityTypeMeta(BALANCE_ENTITY_TYPE_STORAGE)?.fallbackName || "Storage",
            color: "#FF9800"
        },
        {
            typeId: BALANCE_ENTITY_TYPE_DATABASE,
            name: getBalanceEntityTypeMeta(BALANCE_ENTITY_TYPE_DATABASE)?.fallbackName || "Database",
            color: "#4CAF50"
        },
        {
            typeId: BALANCE_ENTITY_TYPE_SITE,
            name: getBalanceEntityTypeMeta(BALANCE_ENTITY_TYPE_SITE)?.fallbackName || "Site",
            color: "#81C784"
        },
        {
            typeId: BALANCE_ENTITY_TYPE_CERTIFICATE,
            name: getBalanceEntityTypeMeta(BALANCE_ENTITY_TYPE_CERTIFICATE)?.fallbackName || "Certificate",
            color: "#F44336"
        }
    ];
}

function resolveDayPayload(payload) {
    const candidates = [payload?.data?.data, payload?.data, payload];

    for (const candidate of candidates) {
        if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
            return candidate;
        }
    }

    return null;
}

export const state = {
    totalData: [],
    dayData: null,
    dayDate: null,
    isLoading: false,
    isDayLoading: false
};

export const mutations = {
    [types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL](state, status) {
        state.isLoading = status;
    },
    [types.CLEAR_CONTRACT_SPENDING_TOTAL_DATA](state) {
        state.totalData = [];
    },
    [types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL_DAY](state, status) {
        state.isDayLoading = status;
    },
    [types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATA](state, data) {
        state.dayData = data && typeof data === "object" ? data : null;
    },
    [types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATE](state, date) {
        state.dayDate = date || null;
    },
    [types.SET_CONTRACT_SPENDING_TOTAL_DATA](state, data) {
        let result = [];

        if (data && typeof data === 'object') {
            const pieEntitySeriesMeta = resolvePieEntitySeriesMeta();
            Object.entries(data).forEach(([, value]) => {
                const entities = normalizeBalanceEntities(value);

                pieEntitySeriesMeta.forEach((entityMeta) => {
                    const entityValue = getBalanceEntityValue(entities, entityMeta.typeId);
                    if (entityValue <= 0) {
                        return;
                    }

                    result.push({
                        name: entityMeta.name,
                        y: entityValue,
                        color: entityMeta.color
                    });
                });
            });
        }

        state.totalData = result;
    }
};

export const actions = {
    async fetchTotalData({ commit }, data) {
        let activeContractId =
            data.contractId || store.getters["contract/activeContract"]?.id;

        if (!activeContractId) {
            commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL, false);
            return undefined;
        }

        commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL, true);

        try {
            const response = await show(activeContractId);
            if (response && response.data && response.data.data) {
                commit(types.SET_CONTRACT_SPENDING_TOTAL_DATA, response.data.data);
            }
            return response;
        } finally {
            commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL, false);
        }
    },
    async fetchTotalDataByDate({ commit }, data = {}) {
        const activeContractId =
            data.contractId || store.getters["contract/activeContract"]?.id;
        const date = typeof data.date === "string" ? data.date.trim() : "";

        if (!activeContractId || date.length === 0) {
            commit(types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATA, null);
            commit(types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATE, null);
            commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL_DAY, false);
            return null;
        }

        commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL_DAY, true);

        try {
            const response = await showByDate(activeContractId, date);
            const resolvedPayload = resolveDayPayload(response?.data);
            commit(types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATA, resolvedPayload);
            commit(types.SET_CONTRACT_SPENDING_TOTAL_DAY_DATE, date);
            return response;
        } finally {
            commit(types.SET_IS_LOADING_CONTRACT_SPENDING_TOTAL_DAY, false);
        }
    }
};

export const getters = {
    totalData: (state) => state.totalData,
    dayData: (state) => state.dayData,
    dayEntities: (state) => normalizeBalanceEntities(state.dayData),
    dayDetails: (state, getters) => getters.dayEntities,
    dayDate: (state) => state.dayDate,
    isLoading: (state) => state.isLoading,
    isDayLoading: (state) => state.isDayLoading
};
